import {
	Paper,
	SortDirection,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
} from "@mui/material"
import { styled } from "@mui/material/styles"
import { visuallyHidden } from "@mui/utils"
import React, { MouseEvent } from "react"
import { useSelector } from "react-redux"

import { useTranslation } from "react-i18next"
import { RootState } from "../../../store"
import { FilesListState, FileState } from "../../../store/actions/file"
import ScrollHint from "../../common/scroll-hint"
import NoData from "../../error/nodata"
import FileRow from "./row"

interface HeadCell {
	id: keyof FileState
	label: string
	numeric: boolean
	disablePadding: boolean
}

interface IEnhancedTable {
	handleDownload: (value: string) => void
	sortCriteria: [{ field: string; order: SortDirection }]
	setSortCriteria: (value: [{ field: string; order: SortDirection }]) => void
}

interface IEnhancedTableHeadProps {
	sortCriteria: [{ field: string; order: SortDirection }]
	onRequestSort: (
		event: React.MouseEvent<unknown>,
		property: keyof FileState
	) => void
}

const TableCellStyled = styled(TableCell)(() => ({
	paddingLeft: "8px",
}))

const TableHeadStyled = styled(TableHead)(() => ({
	backgroundColor: "#E9EDF0",
}))

function EnhancedTableHead(props: IEnhancedTableHeadProps) {
	const { sortCriteria, onRequestSort } = props
	const createSortHandler =
		(property: keyof FileState) => (event: MouseEvent<unknown>) => {
			onRequestSort(event, property)
		}

	const { t } = useTranslation()

	const headCells: HeadCell[] = [
		{
			id: "originalFileName",
			numeric: false,
			disablePadding: true,
			label: t("table.filename", { ns: "distinte" }),
		},
		{
			id: "plicksCount",
			numeric: true,
			disablePadding: false,
			label: t("table.plicksNumber", { ns: "distinte" }),
		},
		{
			id: "idBank",
			numeric: false,
			disablePadding: false,
			label: t("table.bank", { ns: "distinte" }),
		},
		{
			id: "corporateSenderId",
			numeric: false,
			disablePadding: false,
			label: t("table.corporate", { ns: "distinte" }),
		},
		{
			id: "campaignName",
			numeric: false,
			disablePadding: false,
			label: t("table.campaign", { ns: "distinte" }),
		},
		{
			id: "status",
			numeric: false,
			disablePadding: false,
			label: t("table.status", { ns: "distinte" }),
		},
		{
			id: "totalAmount",
			numeric: true,
			disablePadding: false,
			label: t("table.total", { ns: "distinte" }),
		},
		{
			id: "creationDate",
			numeric: false,
			disablePadding: false,
			label: t("table.creationDate", { ns: "distinte" }),
		},
	]

	return (
		<TableHeadStyled>
			<TableRow>
				{headCells.map((headCell: HeadCell) => {
					const cellSorted:
						| { field: string; order: SortDirection }
						| undefined = sortCriteria.find(
						(criteria: { field: string; order: SortDirection }) =>
							criteria.field === headCell.id
					)
					return (
						<TableCellStyled
							key={headCell.id}
							align={headCell.numeric ? "right" : "left"}
							padding={headCell.disablePadding ? "none" : "normal"}
							sortDirection={!!cellSorted ? cellSorted.order : undefined}
						>
							<TableSortLabel
								active={!!cellSorted}
								direction={cellSorted?.order || "asc"}
								onClick={createSortHandler(headCell.id)}
							>
								{headCell.label}{" "}
								{cellSorted ? (
									<span style={visuallyHidden}>
										{" "}
										{cellSorted?.order === "desc"
											? "sorted descending"
											: "sorted ascending"}{" "}
									</span>
								) : null}
							</TableSortLabel>
						</TableCellStyled>
					)
				})}
				<TableCellStyled />
			</TableRow>
		</TableHeadStyled>
	)
}

const EnhancedTable = ({
	handleDownload,
	sortCriteria,
	setSortCriteria,
}: IEnhancedTable) => {
	const { t } = useTranslation()
	const distinteState: FilesListState = useSelector(
		(state: RootState) => state.files
	)

	const handleRequestSort = (
		event: React.MouseEvent<unknown>,
		property: keyof FileState
	) => {
		const cellSorted: { field: string; order: SortDirection } | undefined =
			sortCriteria.find(
				(criteria: { field: string; order: SortDirection }) =>
					criteria.field === property
			)
		const isAsc = !!cellSorted ? cellSorted.order : false
		setSortCriteria([{ field: property, order: isAsc ? "desc" : "asc" }])
	}

	return (
		<>
			<TableContainer component={Paper}>
				<Table
					sx={{ minWidth: 750 }}
					aria-labelledby="tableTitle"
					size="medium"
				>
					<EnhancedTableHead
						sortCriteria={sortCriteria}
						onRequestSort={handleRequestSort}
					/>
					<TableBody>
						{distinteState?.list?.page?.content.map((row: FileState) => (
							<FileRow
								key={`rowfile_${row.id}`}
								handleDownload={handleDownload}
								row={row}
							/>
						))}

						{distinteState?.list?.page?.content?.length === 0 && (
							<TableRow>
								<TableCellStyled
									colSpan={8}
									style={{ textAlign: "center" }}
									component="td"
									scope="row"
									padding="none"
								>
									<NoData />
								</TableCellStyled>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>
			{distinteState?.list?.nextUrl ? (
				<ScrollHint />
			) : (
				<div style={{ textAlign: "center" }}>
					{t("endScroll", { ns: "distinte" })}
				</div>
			)}
		</>
	)
}
export default EnhancedTable
